// @flow
import React, { useState } from 'react';
import type { AppData } from '../types';
import { formatDate } from '../utils';
import styled from 'styled-components';
import { Icon } from 'bench-internal-theme';

type SummaryCardProps = {
  appData: AppData;
};

const Container = styled.div`
  width: 354px;
  border-radius: 4px;
  background-color: #f9fafc;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const BadgeCommon = styled.div`
  border: 1px solid;
  border-radius: 5px;
  font-size: 12px;
  font-weight: bold;
  padding: 0 3px;
  text-transform: uppercase;
`;

const BadgeKeepUp = styled(BadgeCommon)`
  color: #0f7f3e;
  border-color: #0f7f3e;
  background-color: #effdf5;
`;

const Section = styled.div`
  padding: 25px 20px 15px 20px;
  display: flex;
  flex-direction: column;

  &:not(:last-child) {
    border-bottom: 2px solid;
    border-bottom-color: #e4e5e7;
  }
`;

const Detail = styled.div`
  width: 45%;
`;

const SectionRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 8px;
  justify-content: space-between;
`;

const CompanyName = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin: 4px 0;
  wordwrap: break-word;
`;

const LastContact = styled.div`
  font-size: 12px;
  color: #9ea2ac;
`;

const Subtitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  color: #9ea2ac;
  user-select: none;
`;

const Value = styled.div`
  font-size: 14px;
  margin: 2px 0;
  overflow-wrap: break-word;
`;

const NotesHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 20px;
  &:hover {
    cursor: pointer;
    background-color: #f3f5f8;
  }
`;

const NotesText = styled.div`
  background-color: #f9fafc;
  margin: 0;
  padding: 20px;
  font-size: 14px;
  max-height: 512px;
  overflow-y: auto;
  wordwrap: break-word;
`;

const Caret = styled(Icon)`
  margin-left: auto;
  margin-right: 0;
  cursor: pointer;
`;

const SummaryCard = ({ appData }: SummaryCardProps): React.ReactElement => {
  const [showNotes, setShowNotes] = useState(false);
  const SummaryDetail = ({
    title,
    value,
  }: {
    title: string;
    value?: string;
  }): React.ReactElement => (
    <Detail>
      <Subtitle>{title}</Subtitle>
      <Value>{value}</Value>
    </Detail>
  );
  return (
    <Container>
      <Section>
        <Header>
          <Subtitle>{appData.clientId}</Subtitle>
          <BadgeKeepUp>{appData.accountDetails.inboxState || 'N/A'}</BadgeKeepUp>
        </Header>
        <CompanyName>{appData.businessDetails.company || 'N/A'}</CompanyName>
        <LastContact>
          Last contact:{' '}
          {appData.accountDetails.lastContactDate
            ? formatDate(appData.accountDetails.lastContactDate)
            : 'N/A'}
        </LastContact>
      </Section>
      <Section>
        <SectionRow>
          <SummaryDetail
            title={'Customer Name'}
            value={appData.customerDetails.fullName || 'N/A'}
          />
          <SummaryDetail title={'Customer Phone'} value={appData.customerDetails.phone || 'N/A'} />
        </SectionRow>
        <SectionRow>
          <SummaryDetail title={'Customer Email'} value={appData.customerDetails.email || 'N/A'} />
        </SectionRow>
      </Section>
      <Section>
        <SectionRow>
          <SummaryDetail
            title={'Filing Type'}
            value={appData.businessDetails.filingType || 'N/A'}
          />
          <SummaryDetail title={'Structure'} value={appData.businessDetails.structure || 'N/A'} />
        </SectionRow>
        <SectionRow>
          <SummaryDetail title={'Industry'} value={appData.businessDetails.industry || 'N/A'} />
          <SummaryDetail title={'NAICS'} value={appData.businessDetails.naics || 'N/A'} />
        </SectionRow>
      </Section>
      <Section>
        <SectionRow>
          <SummaryDetail
            title={'BPA'}
            value={appData.benchContacts.bookkeeper.primary.fullName || 'N/A'}
          />
          <SummaryDetail title={'Onboarding'} value={'N/A'} />
        </SectionRow>
        <SectionRow>
          <SummaryDetail title={'Catchup'} value={'N/A'} />
          <SummaryDetail title={'Retro'} value={'N/A'} />
        </SectionRow>
        <SectionRow>
          <SummaryDetail title={'Retention'} value={'N/A'} />
          <SummaryDetail title={'Tax Advisor'} value={'N/A'} />
        </SectionRow>
      </Section>
      <NotesHeader onClick={() => setShowNotes(!showNotes)}>
        <Subtitle>BPA Notes</Subtitle>
        {showNotes ? (
          <Caret icon={'caretUp'} color={'#9EA2AC'} />
        ) : (
          <Caret icon={'caretDown'} color={'#9EA2AC'} />
        )}
      </NotesHeader>
      {showNotes ? (
        <NotesText>
          {appData.accountDetails.notes &&
            appData.accountDetails.notes.split('\n').map((line: string, i: number) => (
              <div key={i}>
                {line}
                <br />
              </div>
            ))}
        </NotesText>
      ) : (
        <div></div>
      )}
    </Container>
  );
};

export default SummaryCard;
